// @ts-nocheck
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  Suspense,
  useImperativeHandle,
  Ref,
  forwardRef,
  useMemo,
} from "react";
import { useNavigationStore } from "src/store/navigationStore";

import {
  NavigationData,
  NavigationPath,
  NavigationSequence,
} from "@/types/navigation";
import { useNavigate } from "react-router-dom";
import NavSlider from "@/components/controls/navigation/nav-slider";

import icon_camera from "src/assets/crime/icon_camera.png";
import icon_victimM from "src/assets/crime/icon_victimM.png";
import icon_victimF from "src/assets/crime/icon_victimF.png";
import icon_prisoner from "src/assets/crime/icon_prisoner.png";
import icon_police from "src/assets/crime/icon_police.png";
import icon_citizenF from "src/assets/crime/icon_citizenF.png";
import icon_citizenM from "src/assets/crime/icon_citizenM.png";
import icon_security from "src/assets/crime/icon_security.png";
import icon_paramedic from "src/assets/crime/icon_paramedic.png";

import {
  SceneData,
  CrimeSceneData,
  CharacterTransform,
  SceneItem,
  FrameObject,
} from "@/types/crime";
import { useCrimeSceneStore } from "src/store/crimeSceneStore";

import {
  LOD,
  ALL_SCENES_DATA,
  AUTOPLAY_SPEED,
  SPLAT_MAIN,
  APPEAR_CHARACTER,
} from "src/const/crimeSceneConst";

import { SCENE_DATA } from "src/const/crimeSceneConstExtra";

import {
  getSceneDataBySceneIndex,
  getTimeIndex,
  calculatePercentage,
  findObjectByName,
  findObjectByStringArrayName,
  createCharacterFrameObject,
  getTitleIndex,
  reduceDataByFraction,
  getAvatarDisplayNameByType,
  getAvatarColorByType,
  formatSecondsToHHmm,
  calculateValue,
} from "src/utils/simulation";

import { useAudioStore } from "@/store/audioStore";
import {
  roundToDecimals,
  vec3Distance,
  hexToRgbArray,
  getYAngleDegree,
  angleDifferenceABtoAC,
  wrapAngle,
} from "src/utils/game";
import * as pc from "playcanvas";
import { OrbitControls } from "@playcanvas/react/scripts";
import {
  useTexture,
  useMaterial,
  useEnvAtlas,
  useApp,
  useSplat,
  useModel,
} from "@playcanvas/react/hooks";
import { Application, Entity, Gltf } from "@playcanvas/react";
import {
  Render,
  Camera,
  Light,
  GSplat,
  Script,
  Environment,
} from "@playcanvas/react/components";
import { CameraControls } from "playcanvas/scripts/esm/camera-controls.mjs";

import WatermarkScreen from "src/components/watermark";
import { useLodSplat } from "src/utils/useLodSplat";

export default function CrimeScene() {
  const cameraScriptRef = useRef();
  const sceneObjectRef = useRef<Record<string, Entity>>({});
  const latestFocusingCharacterRef = useRef<string>("observationCamera");
  const latestSceneDataRef = useRef<SceneData>(null);
  const latestSceneIndexRef = useRef<number>(-1);
  const isSimulationAutoPlayingRef = useRef<boolean>(false);

  const latestSceneAnimationTimeArrayRef = useRef<number[]>([]);
  const latestAnimationFrameDataRef = useRef<SceneItem>(null);
  const latestAnimationFrameCharacterDataRef = useRef<
    Record<string, FrameObject>
  >({});

  const latestAnimationCameraTimeDataRef = useRef<SceneItem[]>([]);
  const latestAnimationCameraTimeTimesArrayRef = useRef<number[]>([]);
  const latestSimulationProcessingTimeRef = useRef<number>(-1);
  const isBlockingAutoPlayRef = useRef<boolean>(false);

  const isMouseDraggingRef = useRef<boolean>(false);
  const isMouseDraggingTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const isTouchDraggingRef = useRef<boolean>(false);
  const isTouchDraggingTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const setProgress = useCrimeSceneStore((state) => state.setProgress);
  const setSimulationData = useCrimeSceneStore(
    (state) => state.setSimulationData,
  );
  const setShowObjective = useCrimeSceneStore(
    (state) => state.setShowObjective,
  );
  const setFocusingCharacter = useCrimeSceneStore(
    (state) => state.setFocusingCharacter,
  );
  const setCurrentLevel = useCrimeSceneStore((state) => state.setCurrentLevel);
  const setCurrentLevelIndex = useCrimeSceneStore(
    (state) => state.setCurrentLevelIndex,
  );

  const setCameraPosition = useCrimeSceneStore(
    (state) => state.setCameraPosition,
  );
  const setCameraLookingPosition = useCrimeSceneStore(
    (state) => state.setCameraLookingPosition,
  );
  const setCameraAngle = useCrimeSceneStore((state) => state.setCameraAngle);

  // const setFocusingCharacterPosition = useCrimeSceneStore(
  //   (state) => state.setFocusingCharacterPosition,
  // );
  // const setFocusingCharacterAngle = useCrimeSceneStore(
  //   (state) => state.setFocusingCharacterAngle,
  // );
  // const setNavigationMessage = useNavigationStore(
  //   (state) => state.setNavigationMessage,
  // );
  const setSimulationCheckpoint = useCrimeSceneStore(
    (state) => state.setSimulationCheckpoint,
  );
  const setSceneTime = useCrimeSceneStore((state) => state.setSceneTime);
  const setSceneIndex = useCrimeSceneStore((state) => state.setSceneIndex);
  const setInterrupt = useNavigationStore((state) => state.setInterrupt);
  const setSceneMessages = useCrimeSceneStore(
    (state) => state.setSceneMessages,
  );

  const autoPlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const blockingAutoPlayTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const setIsFinishedAllMc = useCrimeSceneStore(
    (state) => state.setIsFinishedAllMc,
  );

  const latestProgressRef = useRef<number>(0);
  const controls = useRef<any>(null);

  const globalFirstPersonViewPosition = useRef<Record<string, THREE.Object3D>>(
    {},
  );
  const globalIsShowingCharacter = useRef<Record<string, boolean>>({});
  const globalFirstPersonViewTarget = useRef<Record<string, THREE.Object3D>>(
    {},
  );

  const updateCameraEvent = useRef<number>(0);
  const isCameraUpdating = useRef<boolean>(false);

  const characterUIpositionRef = useRef<Record<string, HTMLDivElement | null>>(
    {},
  );
  const character2dUIRef = useRef<Record<string, HTMLDivElement | null>>({});
  const setPause = useAudioStore((s) => s.setPause);
  const setResume = useAudioStore((s) => s.setResume);

  function getModelPathByName(name: string) {
    const result = APPEAR_CHARACTER.find((c) => c.name === name);
    return result?.modelPath ?? "";
  }

  function resetSlider() {
    console.log("reset slider");
    setProgress(0);
    //instantUpdateCamera();
  }

  // function instantUpdateCamera() {
  //   updateCameraEvent.current = Date.now();
  // }

  useEffect(() => {
    setIsFinishedAllMc(false);
    setSceneIndex(0);
    resetSlider();
    return () => {
      console.log("reset interrupt and navigation message");
      setInterrupt(false);
      // setNavigationMessage("");
      setFocusingCharacter("observationCamera");
    };
  }, []);

  function updateScene(sceneIndex: number = 0) {
    const newSceneData: SceneData = getSceneDataBySceneIndex(sceneIndex);
    latestSceneDataRef.current = newSceneData;
    setSceneTime(formatSecondsToHHmm(latestSceneDataRef.current.startTime));
    const newSceneAnimationTimeArray = latestSceneDataRef.current.animation.map(
      (item) => item.time,
    );
    latestSceneAnimationTimeArrayRef.current = newSceneAnimationTimeArray;
    setSimulationData(latestSceneDataRef.current);

    latestAnimationCameraTimeDataRef.current = newSceneData.cameraLevel;
    latestAnimationCameraTimeTimesArrayRef.current =
      latestAnimationCameraTimeDataRef.current.map((item) => item.time);
    setProgress(0);
    setTimeout(() => {
      updateCameraEvent.current = Date.now();
    }, 50);
    setFocusingCharacter("observationCamera");

    setSceneMessages(latestSceneDataRef.current.messages);
  }

  const DebugKeyListener = () => {
    useEffect(() => {
      const handleSpacePress = (event: KeyboardEvent) => {
        // if (event.key === "p") {
        //     console.log(
        //         controls.current.object.position.x +
        //             "," +
        //             controls.current.object.position.y +
        //             "," +
        //             controls.current.object.position.z,
        //     );
        //     console.log(
        //         controls.current.object.quaternion.x +
        //             "," +
        //             controls.current.object.quaternion.y +
        //             "," +
        //             controls.current.object.quaternion.z +
        //             "," +
        //             controls.current.object.quaternion.w,
        //     );
        // }
      };
      window.addEventListener("keydown", handleSpacePress);
      return () => {
        window.removeEventListener("keydown", handleSpacePress);
      };
    }, []);
    return <></>;
  };

  // const Capture = () => {
  //     const { gl, scene, camera } = useThree();
  //     gl.render(scene, camera);
  //     useEffect(() => {
  //         const handleSpacePress = (event: KeyboardEvent) => {
  //             if (event.key === "r") {
  //                 const dataURL = gl.domElement.toDataURL("image/png");
  //                 const link = document.createElement("a");
  //                 link.href = dataURL;
  //                 link.download = `screenshot-${Date.now()}.png`;
  //                 link.click();
  //                 link.remove();
  //             }
  //         };
  //         window.addEventListener("keydown", handleSpacePress);
  //         return () => {
  //             window.removeEventListener("keydown", handleSpacePress);
  //         };
  //     }, []);
  //     return <></>;
  // };

  // interface CharacterUIProps {
  //   name: string;
  //   type: string;
  // }
  // interface CharacterUIHandle {}
  // const CharacterUI = forwardRef<CharacterUIHandle, CharacterUIProps>(
  //   (props, ref: Ref<CharacterUIHandle>) => {
  //     return (
  //       <div
  //         className="absolute -translate-x-1/2 -translate-y-1/2"
  //         ref={(el) => {
  //           character2dUIRef.current[props.name] = el;
  //         }}
  //       >
  //         <div className="flex items-center gap-2 z-0">
  //           <div
  //             className={`w-2 h-2 rounded-full`}
  //             style={{
  //               backgroundColor: getAvatarColorByType(props.type),
  //             }}
  //           ></div>
  //           <span className="text-white text-center [text-shadow:-1px_-1px_0_#000,1px_-1px_0_#000,-1px_1px_0_#000,1px_1px_0_#000]">
  //             {getAvatarDisplayNameByType(props.type)}
  //           </span>
  //         </div>
  //       </div>
  //     );
  //   },
  // );

  // const SceneUI = () => {
  //   return (
  //     <div className="fixed top-0 left-0 w-[100%] h-[100%] pointer-events-none">
  //       {APPEAR_CHARACTER.map((character: any, index) => (
  //         <CharacterUI
  //           key={index}
  //           name={character.name}
  //           type={character.type}
  //         />
  //       ))}
  //     </div>
  //   );
  // };

  const Character2dUI = ({ name, type }: { name: string; type: string }) => {
    return (
      <div
        className="absolute -translate-x-1/2 -translate-y-1/2"
        ref={(el) => {
          character2dUIRef.current[name] = el;
        }}
      >
        <div className="flex items-center gap-2 z-0">
          <div
            className={`w-2 h-2 rounded-full`}
            style={{
              backgroundColor: getAvatarColorByType(type),
            }}
          ></div>
          <span className="text-white text-center [text-shadow:-1px_-1px_0_#000,1px_-1px_0_#000,-1px_1px_0_#000,1px_1px_0_#000]">
            {getAvatarDisplayNameByType(type)}
          </span>
        </div>
      </div>
    );
  };

  const Scene2dUI = () => {
    return (
      <div className="fixed top-0 left-0 w-[100%] h-[100%] pointer-events-none">
        {APPEAR_CHARACTER.map((character: any, index) => (
          <Character2dUI
            key={index}
            name={character.name}
            type={character.type}
          />
        ))}
      </div>
    );
  };

  const ProgressController = () => {
    const sceneIndex = useCrimeSceneStore((state) => state.sceneIndex);
    useEffect(() => {
      if (latestSceneIndexRef.current != sceneIndex) {
        latestSceneIndexRef.current = sceneIndex;
        stopAutoPlay();
        updateScene(sceneIndex);
      }
    }, [sceneIndex]);

    const progress = useCrimeSceneStore((state) => state.progress);
    useEffect(() => {
      latestProgressRef.current = progress;
      isCameraUpdating.current = true;
      if (latestSceneDataRef.current == null) {
        return;
      }
      //calculate display time
      const processingTime =
        latestSceneDataRef.current.animationTotalTime * progress;
      const latestDisplaySecond = calculateValue(
        latestSceneDataRef.current.startTime,
        latestSceneDataRef.current.endTime,
        progress,
      );
      setSceneTime(formatSecondsToHHmm(latestDisplaySecond));

      //update character data
      const sceneAnimationDataIndex = getTimeIndex(
        latestSceneAnimationTimeArrayRef.current,
        processingTime,
      );

      if (sceneAnimationDataIndex != -1) {
        latestAnimationFrameDataRef.current =
          latestSceneDataRef.current.animation[sceneAnimationDataIndex];
        const allCharactersData = latestAnimationFrameDataRef.current.data;
        latestAnimationFrameCharacterDataRef.current = {};
        for (let index = 0; index < allCharactersData.length; index++) {
          const characterFrameObject = createCharacterFrameObject(
            allCharactersData[index],
          );
          latestAnimationFrameCharacterDataRef.current = {
            ...latestAnimationFrameCharacterDataRef.current,
            [characterFrameObject.name]: characterFrameObject,
          };
        }
      }
    }, [progress]);

    const hasFinishSceneMessage = useCrimeSceneStore(
      (state) => state.hasFinishSceneMessage,
    );
    useEffect(() => {
      if (hasFinishSceneMessage && progress === 1) {
        setTimeout(() => setShowObjective(true), 1000);
      }
    }, [hasFinishSceneMessage, progress]);

    return <></>;
  };

  function stopAutoPlay() {
    isSimulationAutoPlayingRef.current = false;
    if (autoPlayTimeoutRef.current) {
      clearTimeout(autoPlayTimeoutRef.current);
      autoPlayTimeoutRef.current = null;
    }
    //instantUpdateCamera();
    if (latestProgressRef.current < 0.99) {
      setPause();
    }
  }

  function startAutoPlay() {
    isSimulationAutoPlayingRef.current = true;
    setResume();
  }

  function resumeAutoPlay(milliseconds: number) {
    if (isBlockingAutoPlayRef.current) {
      console.log("blocking");
      return;
    }
    if (milliseconds > 0) {
      stopAutoPlay();
    }
    autoPlayTimeoutRef.current = setTimeout(() => {
      autoPlayTimeoutRef.current = null;
      startAutoPlay();
    }, milliseconds);
  }

  const DataController = () => {
    const focusingCharacter = useCrimeSceneStore(
      (state) => state.focusingCharacter,
    );
    useEffect(() => {
      latestFocusingCharacterRef.current =
        focusingCharacter == "" ? "observationCamera" : focusingCharacter;
    }, [focusingCharacter]);
    return <></>;
  };

  const SimulationAutoPlayController = () => {
    const app = useApp();
    let eachProgress = -1;
    let _newProgress = 0;
    const releaseDelay = 2000;
    let _currentMouse = 0;
    let _currentTouch = 0;

    const startAutoPlayData = useCrimeSceneStore(
      (state) => state.startAutoPlayData,
    );
    const stopAutoPlayData = useCrimeSceneStore(
      (state) => state.stopAutoPlayData,
    );
    useEffect(() => {
      if (startAutoPlayData.time != -1) {
        resumeAutoPlay(startAutoPlayData.delay);
      }
    }, [startAutoPlayData]);
    useEffect(() => {
      if (stopAutoPlayData != -1) {
        stopAutoPlay();
      }
    }, [stopAutoPlayData]);

    useEffect(() => {
      app.on("update", frameUpdate);
      return () => app.off("update", frameUpdate);
    }, [app]);

    const frameUpdate = (dt: number) => {
      let isMouseUpdated =
        _currentMouse != cameraScriptRef.current._state.mouse[0];
      _currentMouse = cameraScriptRef.current._state.mouse[0];
      let isTouchUpdated =
        _currentTouch != cameraScriptRef.current._state.touches;
      _currentTouch = cameraScriptRef.current._state.touches;

      if (isMouseUpdated) {
        if (_currentMouse == 1) {
          console.log("mouse drag");
          stopAutoPlay();
          if (isMouseDraggingTimeoutRef.current != null) {
            clearTimeout(isMouseDraggingTimeoutRef.current);
            isMouseDraggingTimeoutRef.current = null;
          }
          isMouseDraggingRef.current = true;
        } else {
          console.log("mouse release");
          if (isMouseDraggingTimeoutRef.current != null) {
            clearTimeout(isMouseDraggingTimeoutRef.current);
            isMouseDraggingTimeoutRef.current = null;
          }
          isMouseDraggingTimeoutRef.current = setTimeout(() => {
            isMouseDraggingRef.current = false;
            resumeAutoPlay(0);
          }, releaseDelay);
        }
      }

      if (isTouchUpdated) {
        if (_currentTouch >= 1) {
          console.log("touch drag");
          stopAutoPlay();
          if (isTouchDraggingTimeoutRef.current != null) {
            clearTimeout(isTouchDraggingTimeoutRef.current);
            isTouchDraggingTimeoutRef.current = null;
          }
          isTouchDraggingRef.current = true;
        } else {
          console.log("touch release");
          if (isTouchDraggingTimeoutRef.current != null) {
            clearTimeout(isTouchDraggingTimeoutRef.current);
            isTouchDraggingTimeoutRef.current = null;
          }
          isTouchDraggingTimeoutRef.current = setTimeout(() => {
            isTouchDraggingRef.current = false;
            resumeAutoPlay(0);
          }, releaseDelay);
        }
      }

      if (
        isSimulationAutoPlayingRef.current == false ||
        latestSceneDataRef.current == null
      ) {
        return;
      }
      eachProgress =
        (dt / latestSceneDataRef.current.animationTotalTime) * AUTOPLAY_SPEED;
      _newProgress =
        latestProgressRef.current + eachProgress > 1
          ? 1
          : latestProgressRef.current + eachProgress;
      setProgress(_newProgress);
      if (latestProgressRef.current + eachProgress > 1) {
        stopAutoPlay();
      }
    };
    return <></>;
  };

  const MainCamera = () => {
    const app = useApp();
    let _localProgress;
    let _localFocusingCharacter;
    let focusingObjectPosition;
    let finalCameraPosition;
    let finalCameraLookingPosition;

    useEffect(() => {
      app.on("update", frameUpdate);
      return () => app.off("update", frameUpdate);
    }, [app]);

    const frameUpdate = (dt: number) => {
      let isProgressUpdated = _localProgress != latestProgressRef.current;
      _localProgress = latestProgressRef.current;
      let isFocusingCharacterUpdated =
        _localFocusingCharacter != latestFocusingCharacterRef.current;
      _localFocusingCharacter = latestFocusingCharacterRef.current;
      if (
        cameraScriptRef.current == null ||
        sceneObjectRef.current == null ||
        sceneObjectRef.current[_localFocusingCharacter] == null ||
        sceneObjectRef.current[_localFocusingCharacter].findByName("inner") ==
          null
      ) {
        return;
      }
      if (
        _localProgress == 0 ||
        isProgressUpdated ||
        isFocusingCharacterUpdated
      ) {
        const focusingObject =
          sceneObjectRef.current[_localFocusingCharacter].findByName("inner");
        if (latestFocusingCharacterRef.current == "observationCamera") {
          focusingObjectPosition = focusingObject.getPosition().clone();
        } else {
          focusingObjectPosition = new pc.Vec3(
            focusingObject.getPosition().x,
            focusingObject.getPosition().y + 0.2,
            focusingObject.getPosition().z,
          );
        }
        const forward = focusingObject.forward.clone().mulScalar(0.1);
        finalCameraLookingPosition = focusingObjectPosition.clone();
        finalCameraPosition = focusingObjectPosition.add(forward);

        cameraScriptRef.current.reset(
          finalCameraLookingPosition,
          finalCameraPosition,
        );

        const cameraAngle = getYAngleDegree(
          finalCameraPosition,
          finalCameraLookingPosition,
        );

        setCameraAngle(cameraAngle);
        setCameraPosition(finalCameraPosition);
        setCameraLookingPosition(finalCameraLookingPosition);

        setCurrentLevel(finalCameraPosition.y > 0.5 ? "L2" : "L1");
        setCurrentLevelIndex(finalCameraPosition.y > 0.5 ? "2" : "1");
      }
    };

    useEffect(() => {
      const defaultCameraLookingPosition = new pc.Vec3(0, 0, 1);
      const defaultPosition = new pc.Vec3(0, 0, 0);
      cameraScriptRef.current.reset(
        defaultCameraLookingPosition,
        defaultPosition,
      );
    }, []);

    return (
      <Entity>
        <Camera />
        <Script
          ref={cameraScriptRef}
          script={CameraControls}
          // enableFly={false}
        />
      </Entity>
    );
  };

  const SceneAvatar = ({ modelPath }: { name: string }) => {
    const app = useApp();
    const avatarEntityRef = useRef();
    const { asset: model } = useModel(modelPath);
    const changeMaterial = (entity) => {
      const renders = entity.findComponents("render");
      renders.forEach((render) => {
        if (render.meshInstances) {
          render.meshInstances.forEach((meshInstance) => {
            const material = meshInstance.material;
            if (material) {
              material.emissive =
                material.diffuse?.clone() || new pc.Color(0, 0, 0);

              if (material.diffuseMap) {
                material.emissiveMap = material.diffuseMap;
              }

              material.emissiveIntensity = 1.0;
              material.useLighting = false;
              material.update();
            }
          });
        }
      });
    };
    useEffect(() => {
      if (model && avatarEntityRef.current) {
        requestAnimationFrame(() => {
          changeMaterial(avatarEntityRef.current);
        });
      }
    }, [model]);

    return model ? (
      <Entity position={[0, 0, 0]}>
        <Entity
          ref={avatarEntityRef}
          rotation={[0, 0, 0]}
          position={[0, 0, 0]}
          scale={[0.05, 0.05, 0.05]}
        >
          <Render type="asset" asset={model} />
        </Entity>
      </Entity>
    ) : (
      <></>
    );
  };

  const SceenObject = ({ name }: { name: string }) => {
    const app = useApp();

    const [isShowObjectState, setIsShowObjectState] = useState<boolean>(false);
    const [modelPathState, setModelPathState] = useState<string>("");

    const _targetPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const _targetRotationRef = useRef<pc.Quat>(new pc.Quat(0, 0, 0, 0));
    const _uiPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));

    useEffect(() => {
      const modelPath = getModelPathByName(name);
      setModelPathState(modelPath);
    }, []);

    useEffect(() => {
      app.on("update", frameUpdate);
      return () => app.off("update", frameUpdate);
    }, [app]);

    const frameUpdate = (dt: number) => {
      if (name in latestAnimationFrameCharacterDataRef.current) {
        setIsShowObjectState(latestFocusingCharacterRef.current != name);
        const objectPosition =
          latestAnimationFrameCharacterDataRef.current[name].position;
        _targetPositionRef.current.set(
          objectPosition.x,
          objectPosition.y,
          -objectPosition.z,
        );
        const objectRotation =
          latestAnimationFrameCharacterDataRef.current[name].rotation;
        _targetRotationRef.current.set(
          objectRotation.x,
          objectRotation.y,
          -objectRotation.z,
          -objectRotation.w,
        );

        const lerpPosition = sceneObjectRef.current[name].getPosition();
        lerpPosition.lerp(lerpPosition, _targetPositionRef.current, dt * 5);
        sceneObjectRef.current[name].setPosition(lerpPosition);

        const lerpRotation = sceneObjectRef.current[name].getRotation();
        lerpRotation.lerp(lerpRotation, _targetRotationRef.current, dt * 5);
        sceneObjectRef.current[name].setRotation(lerpRotation);

        if (
          character2dUIRef.current[name] != null &&
          cameraScriptRef.current != null
        ) {
          _uiPositionRef.current.set(
            lerpPosition.x,
            lerpPosition.y + 0.25,
            lerpPosition.z,
          );
          let uiDistance = vec3Distance(
            _uiPositionRef.current,
            cameraScriptRef.current.entity.getPosition(),
          );
          const screenPos = cameraScriptRef.current._camera.worldToScreen(
            _uiPositionRef.current,
          );
          if (
            uiDistance > 3 ||
            screenPos.x < 0 ||
            screenPos.x > window.innerWidth ||
            screenPos.y < 0 ||
            screenPos.y > window.innerHeight
          ) {
            character2dUIRef.current[name].style.display = "none";
          } else {
            character2dUIRef.current[name].style.display = "";
            character2dUIRef.current[name].style.left = screenPos.x + "px";
            character2dUIRef.current[name].style.top = screenPos.y + "px";
            character2dUIRef.current[name].style.zIndex = screenPos.z;
          }
        }
      } else {
        if (
          character2dUIRef.current[name] != null &&
          cameraScriptRef.current != null
        ) {
          character2dUIRef.current[name].style.display = "none";
        }
        setIsShowObjectState(false);
      }
    };

    return (
      <Entity
        ref={(el) => {
          sceneObjectRef.current[name] = el;
        }}
      >
        <Entity name={"inner"} rotation={[0, 180, 0]}>
          {isShowObjectState ? (
            <Entity>
              {modelPathState != "" ? (
                <SceneAvatar modelPath={modelPathState} />
              ) : (
                <></>
              )}
            </Entity>
          ) : (
            <></>
          )}
        </Entity>
      </Entity>
    );
  };

  const VenueLod = () => {
    const setLoading = useNavigationStore((state) => state.setLoading);
    const app = useApp();
    const gsplatRef = useRef();
    const { asset, loading, error, lodDistances } = useLodSplat(LOD);

    useEffect(() => {
      if (!asset) {
        return;
      }
      const gsplatEntity = gsplatRef.current;
      if (!gsplatRef.current || !app) return;
      gsplatRef.current.gsplat.enabled = true;
      gsplatRef.current.gsplat.unified = true;
      gsplatRef.current.gsplat.asset = asset;
      gsplatRef.current.gsplat.lodDistances = [20, 100, 500];
      gsplatRef.current.gsplat.enabled = true;
    }, [asset]);

    return (
      <Entity
        ref={gsplatRef}
        rotation={[180, 0, 0]}
        position={[0, 0, 0]}
        scale={[1, 1, 1]}
      >
        <GSplat />
      </Entity>
    );
  };

  const PlaycanvasScene = () => {
    const app = useApp();
    return (
      <>
        <SimulationAutoPlayController />
        <SceenObject name={"observationCamera"} />
        {APPEAR_CHARACTER.map((character: any, index) => (
          <SceenObject
            key={index}
            name={character.name}
            type={character.type}
          />
        ))}

        <VenueLod />
        <MainCamera />
      </>
    );
  };

  const Scene = () => {
    return (
      <Application>
        <PlaycanvasScene />
      </Application>
    );
  };

  console.log(
    "simulation component rendered , if this keep showing, something wrong",
  );

  return (
    <>
      <div className="absolute top-0 left-0 w-full h-full">
        <DataController />
        <ProgressController />
        <Scene2dUI />
        <Scene />
        <WatermarkScreen />
      </div>
    </>
  );
}
